import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "~/shared/api/client";
import { createMutationHook } from "~/shared/api/query-factories";

/** Базовый путь коллекции сцен (список/создание, без id) — используется по всему файлу. */
const SCENES_PATH = "/v2/scenes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SceneImage {
    id: string;
    url: string | null;
    key: string | null;
}

export interface Scene {
    id: string;
    name: string;
    description: string | null;
    note: string | null;
    projectId: string;
    status: string;
    creatorId: string | null;
    creatorEmail: string | null;
    creatorName: string | null;
    chatCount: number;
    /** Image attachments; the first one is shown as the scene preview. */
    images: SceneImage[];
    /** Convenience: URL of the first image (or null). Mirrors `images[0]?.url`. */
    previewUrl: string | null;
    createdAt: string;
    updatedAt: string | null;
    [key: string]: unknown;
}

interface SceneListResult {
    data: Scene[];
    total: number;
    pagination: { page: number; limit: number; total: number };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const sceneKeys = {
    all: ["scenes"] as const,
    list: (projectId: string) => ["scenes", "list", projectId] as const,
};

// ---------------------------------------------------------------------------
// Fetch functions
// ---------------------------------------------------------------------------

async function fetchScenesByProject(input: {
    projectId: string;
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
}): Promise<SceneListResult> {
    const { projectId, includeArchived = false, limit = 50, offset = 0 } = input;
    const data = await apiGet<Scene[]>(SCENES_PATH, {
        projectId,
        includeArchived,
        limit,
        offset,
    });
    const scenes = data ?? [];
    return {
        data: scenes,
        total: scenes.length,
        pagination: { page: 1, limit, total: scenes.length },
    };
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useScenesByProject(projectId: string | undefined, options?: { includeArchived?: boolean }) {
    return useQuery({
        queryKey: sceneKeys.list(projectId ?? ""),
        queryFn: () =>
            fetchScenesByProject({ projectId: projectId!, includeArchived: options?.includeArchived }),
        enabled: !!projectId,
    });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export const useCreateSceneSimple = createMutationHook<
    Scene,
    { projectId: string; name: string; description?: string }
>({
    mutationFn: (input) => apiPost<Scene>(SCENES_PATH, input),
    invalidate: [sceneKeys.all],
});

export const useDeleteScene = createMutationHook<unknown, { sceneId: string }>({
    mutationFn: (input) => apiDelete(`/v2/scenes/${input.sceneId}`),
    invalidate: [sceneKeys.all],
});

export const useArchiveScene = createMutationHook<unknown, { sceneId: string }>({
    mutationFn: (input) => apiPost(`/v2/scenes/${input.sceneId}/archive`),
    invalidate: [sceneKeys.all],
});

export const useUnarchiveScene = createMutationHook<unknown, { sceneId: string }>({
    mutationFn: (input) => apiPost(`/v2/scenes/${input.sceneId}/unarchive`),
    invalidate: [sceneKeys.all],
});

export const useUpdateScene = createMutationHook<
    unknown,
    { sceneId: string; name?: string; note?: string; description?: string }
>({
    mutationFn: ({ sceneId, ...body }) => apiPatch(`/v2/scenes/${sceneId}`, body),
    invalidate: [sceneKeys.all],
});

export const useRemoveAttachment = createMutationHook<unknown, { attachmentId: string }>({
    mutationFn: (input) => apiDelete(`/v2/scenes/attachments/${input.attachmentId}`),
    invalidate: [sceneKeys.all],
});

// Attach an already-uploaded image (S3 key/url) to a scene as an attachment.
export const useAddSceneImage = createMutationHook<
    SceneImage,
    { sceneId: string; projectId: string; key: string; url: string; fileName?: string }
>({
    mutationFn: ({ sceneId, projectId, key, url, fileName }) =>
        apiPost<SceneImage>(`/v2/scenes/${sceneId}/attachments`, {
            type: "image",
            projectId,
            key,
            url,
            fileName,
        }),
    invalidate: [sceneKeys.all],
});
